import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiResponse } from 'further-types/api';
import { api } from 'lib/httpClient';
import { useNotification } from 'hooks/ui/useNotification';
import useApiExceptionHandler from 'hooks/ui/useApiExceptionHandler';
import { format } from 'date-fns';
import downloadFile from 'utils/downloadFile';

export function useFund() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const handleApiException = useApiExceptionHandler();

  const duplicate = useMutation({
    throwOnError: false,
    mutationFn: async (trancheId: string) => {
      const { data: result } = await api.get<ApiResponse<unknown>>(
        `fund/copy-fund/${trancheId}`,
      );

      return result.data;
    },
    onSuccess: async () => {
      notification.success(
        'Tranche copied successfully. You may now edit the copied tranche.',
      );
      await queryClient.invalidateQueries({ queryKey: ['tranches'] });
    },
    onError: (error: Error) => {
      handleApiException(error);
    },
  });

  const exportFund = async (
    trancheId: string,
    trancheName: string,
    firmName: string,
  ) => {
    const response = await api.get(`fund/${trancheId}/export`, {
      responseType: 'blob',
    });

    const contentType = response.headers['content-type'];
    if (
      response.status !== 200 ||
      !(
        typeof contentType === 'string' &&
        contentType.startsWith('application/vnd.openxmlformats')
      )
    ) {
      throw Error('Unexpected export response.');
    }

    //$firmname-$tranchename-$dateofdownload-$randomgen5digits
    downloadFile(
      response.data,
      `${firmName}-${trancheName}-${format(
        new Date(),
        'dd/MM/yyyy',
      )}-${Math.floor(Math.random() * 100000)}.xlsx`,
    );
  };

  return {
    duplicate,
    exportFund,
  };
}
